/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function, no-nested-ternary */

import { Icon, InputText } from "@bluframe/blublocks"
import {
  AutosuggestSearchItem,
  HomeVideoReviewSearchItem,
  HomeVideoSearchItem
} from "@bluframe/mightychroma-mightytool-frontend-types"
import getHomeVideoDiscsAndEditions from "helpers/getHomeVideoDiscsAndEditions"
import colors from "styles/colors"

import { ComponentProps } from "."
import { List, Wrapper } from "./styled"
import Suggestion from "./Suggestion"

const getSuggestionTitle = (
  suggestion: AutosuggestSearchItem,
  isHomeVideoReviewSuggestion: boolean
) => {
  if (!isHomeVideoReviewSuggestion) return suggestion.name

  const year = (suggestion as HomeVideoReviewSearchItem).mas?.year
  if (year === undefined) return suggestion.name

  return `${suggestion.name} (${year})`
}

const Autosuggest = ({
  disabled,
  getItemProps,
  getMenuProps,
  hasSelectedIcon,
  inputProps: { id, ref, ...inputProps },
  isLoading,
  isOpen,
  label,
  name,
  onKeyDown,
  onSuggestionClick,
  suggestions
}: ComponentProps) => (
  <Wrapper aria-label={`${name} Autosuggest`}>
    <InputText
      disabled={disabled}
      iconEnd={
        hasSelectedIcon ? (
          <Icon
            color={colors.atlantis}
            group="fa6"
            name="FaCircleCheck"
            title="Item is Selected"
          />
        ) : undefined
      }
      inputId={id}
      inputProps={inputProps}
      label={label}
      name="search"
      onKeyDown={onKeyDown}
      ref={ref}
      value={inputProps.value}
    />

    <List {...getMenuProps()}>
      {isOpen ? (
        isLoading ? (
          <Suggestion
            ariaLabel="Loading"
            isLoading
            itemProps={{
              "aria-disabled": false,
              "aria-selected": false,
              id: "loading",
              role: "option"
            }}
          />
        ) : (
          suggestions.map((suggestion, index) => {
            const isHomeVideoSuggestion =
              "isBluRay" in suggestion && "isUHD" in suggestion
            const isHomeVideoReviewSuggestion =
              !isHomeVideoSuggestion &&
              (suggestion as HomeVideoReviewSearchItem).mas?.year !== undefined

            const title = getSuggestionTitle(
              suggestion,
              isHomeVideoReviewSuggestion
            )

            let subtitlePrimary: string | undefined
            let subtitleSecondary: string | undefined
            const pills: {
              id: string
              label: string
              tone?: "success" | "warning" | "info"
            }[] = []

            const homeVideoData = isHomeVideoSuggestion
              ? (suggestion as HomeVideoSearchItem)
              : ((suggestion as HomeVideoReviewSearchItem).homeVideo ??
                undefined)

            if (homeVideoData) {
              const { subtitlePrimary: primary, subtitleSecondary: secondary } =
                getHomeVideoDiscsAndEditions({
                  editions: Array.isArray(homeVideoData.editions)
                    ? homeVideoData.editions
                    : null,
                  isBluRay: Boolean(homeVideoData.isBluRay),
                  isUHD: Boolean(homeVideoData.isUHD)
                })

              subtitlePrimary = primary
              subtitleSecondary = secondary
            }

            if (isHomeVideoSuggestion) {
              const homeVideoReviewMeta = (
                suggestion as HomeVideoSearchItem & {
                  homeVideoReview?: { id?: string | null }
                }
              ).homeVideoReview
              const hasReview = Boolean(homeVideoReviewMeta?.id)
              pills.push({
                id: `${suggestion.id}-review-pill`,
                label: hasReview ? "Review present" : "No review",
                tone: hasReview ? "success" : "warning"
              })
            } else if (isHomeVideoReviewSuggestion) {
              pills.push({
                id: `${suggestion.id}-review-pill`,
                label: "Review present",
                tone: "success"
              })
            }

            const newsCount = (
              homeVideoData as
                | (HomeVideoSearchItem & { newsCount?: number })
                | undefined
            )?.newsCount
            if (typeof newsCount === "number" && newsCount > 0) {
              pills.push({
                id: `${suggestion.id}-news-pill`,
                label: `News: ${newsCount}`,
                tone: "info"
              })
            }

            const ariaLabelParts = [title]
            if (subtitlePrimary) ariaLabelParts.push(subtitlePrimary)
            if (subtitleSecondary) ariaLabelParts.push(subtitleSecondary)
            const ariaLabel = ariaLabelParts.join(" — ")

            return (
              <Suggestion
                ariaLabel={ariaLabel}
                id={suggestion.id}
                image={(suggestion as HomeVideoSearchItem).images?.[0]}
                itemProps={getItemProps({ index, item: suggestion })}
                key={suggestion.id}
                onSuggestionClick={onSuggestionClick}
                pills={pills}
                subtitlePrimary={subtitlePrimary}
                subtitleSecondary={subtitleSecondary}
                title={title}
              />
            )
          })
        )
      ) : null}
    </List>
  </Wrapper>
)

export default Autosuggest
